import { ensureFreshToken } from '@/shared/api/ensureFreshToken'
import { ChatMessageSchema, type ChatMessage } from '@/entities/chat/api/types'

export type ChatStreamStatus = 'connecting' | 'live' | 'reconnecting' | 'ended'

export type ChatFrame =
  | { type: 'message.created'; conversationId: number; message: ChatMessage }
  | { type: 'message.deleted'; conversationId: number; messageId: number }
  | {
      type: 'conversation.updated'
      conversationId: number
      lastMessageAt: string | null
      lastMessagePreview: string | null
      lastSenderName: string | null
    }
  | { type: 'broadcast.created'; broadcastId: number }
  | {
      type: 'conversation.cursor'
      conversationId: number
      userId: number | null
      role: string
      lastDeliveredMessageId: number | null
      lastReadMessageId: number | null
    }

export type RawFrame = { event: string; data: string }

export function parseSseChunk(buffer: string): { frames: RawFrame[]; rest: string } {
  const normalised = buffer.replace(/\r\n/g, '\n')
  const blocks = normalised.split('\n\n')
  const rest = blocks.pop() ?? ''
  const frames: RawFrame[] = []

  for (const block of blocks) {
    let event = ''
    let data = ''
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) data += line.slice(5).trim()
    }
    if (event) frames.push({ event, data })
  }

  return { frames, rest }
}

export function toChatFrame(raw: RawFrame): ChatFrame | null {
  let payload: Record<string, unknown>
  try {
    payload = JSON.parse(raw.data || '{}') as Record<string, unknown>
  } catch {
    return null
  }

  const conversationId = Number(payload.conversationId)

  switch (raw.event) {
    case 'message.created': {
      const message = ChatMessageSchema.safeParse(payload.message)
      if (!message.success || !Number.isFinite(conversationId)) return null
      return { type: 'message.created', conversationId, message: message.data }
    }
    case 'message.deleted':
      if (!Number.isFinite(conversationId) || !Number.isFinite(Number(payload.messageId))) return null
      return { type: 'message.deleted', conversationId, messageId: Number(payload.messageId) }
    case 'conversation.updated':
      if (!Number.isFinite(conversationId)) return null
      return {
        type: 'conversation.updated',
        conversationId,
        lastMessageAt: (payload.lastMessageAt as string) ?? null,
        lastMessagePreview: (payload.lastMessagePreview as string) ?? null,
        lastSenderName: (payload.lastSenderName as string) ?? null,
      }
    case 'broadcast.created':
      if (!Number.isFinite(Number(payload.broadcastId))) return null
      return { type: 'broadcast.created', broadcastId: Number(payload.broadcastId) }
    case 'conversation.cursor':
      if (!Number.isFinite(conversationId)) return null
      return {
        type: 'conversation.cursor',
        conversationId,
        userId: payload.userId == null ? null : Number(payload.userId),
        role: String(payload.role ?? ''),
        lastDeliveredMessageId:
          payload.lastDeliveredMessageId == null ? null : Number(payload.lastDeliveredMessageId),
        lastReadMessageId: payload.lastReadMessageId == null ? null : Number(payload.lastReadMessageId),
      }
    default:
      return null
  }
}

const WATCHDOG_MS = 45_000
const CONNECT_TIMEOUT_MS = WATCHDOG_MS
const MAX_BACKOFF_MS = 30_000

export type ChatStreamHandlers = {
  onFrame: (frame: ChatFrame) => void
  onStatus: (status: ChatStreamStatus) => void
  onAccessEnded: () => void
}

function backoffDelay(attempt: number): number {
  return Math.random() * Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt)
}

export function openChatEventStream(accountId: number, handlers: ChatStreamHandlers): () => void {
  let stopped = false
  let attempt = 0
  let controller: AbortController | null = null
  let watchdog: ReturnType<typeof setTimeout> | null = null
  let connectTimer: ReturnType<typeof setTimeout> | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const clearWatchdog = () => {
    if (watchdog) clearTimeout(watchdog)
    watchdog = null
  }

  const armWatchdog = () => {
    clearWatchdog()
    watchdog = setTimeout(() => controller?.abort(), WATCHDOG_MS)
  }

  const clearConnectTimer = () => {
    if (connectTimer) clearTimeout(connectTimer)
    connectTimer = null
  }

  const armConnectTimer = () => {
    clearConnectTimer()
    connectTimer = setTimeout(() => controller?.abort(), CONNECT_TIMEOUT_MS)
  }

  const run = async () => {
    while (!stopped) {
      handlers.onStatus(attempt === 0 ? 'connecting' : 'reconnecting')
      controller = new AbortController()
      let forceRefresh = false

      try {
        const token = await ensureFreshToken()
        armConnectTimer()
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/messaging/events`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Account-Id': String(accountId),
            'X-Portal': 'gms',
            Accept: 'text/event-stream',
          },
          credentials: 'include',
          signal: controller.signal,
        })
        clearConnectTimer()

        if (response.status === 401 || response.status === 403) {
          handlers.onStatus('ended')
          handlers.onAccessEnded()
          return
        }

        if (!response.ok || !response.body) {
          throw new Error(`chat stream ${response.status}`)
        }

        attempt = 0
        handlers.onStatus('live')
        armWatchdog()

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
        let buffer = ''

        while (!stopped) {
          const { value, done } = await reader.read()
          if (done) break
          if (stopped) break
          armWatchdog()
          buffer += value
          const parsed = parseSseChunk(buffer)
          buffer = parsed.rest
          for (const raw of parsed.frames) {
            if (stopped) break
            if (raw.event === 'reauth') {
              forceRefresh = true
              stopReading(reader)
              break
            }
            const frame = toChatFrame(raw)
            if (frame) handlers.onFrame(frame)
          }
          if (forceRefresh) break
        }
      } catch {
        attempt = Math.min(attempt + 1, 10)
      } finally {
        clearConnectTimer()
        clearWatchdog()
        controller?.abort()
        controller = null
      }

      if (stopped) return

      if (forceRefresh) {
        try {
          await ensureFreshToken(true)
          attempt = 0
          continue
        } catch {
          handlers.onStatus('ended')
          return
        }
      }

      handlers.onStatus('reconnecting')
      await new Promise<void>((resolve) => {
        retryTimer = setTimeout(resolve, backoffDelay(attempt))
      })
    }
  }

  void run()

  return () => {
    stopped = true
    clearConnectTimer()
    clearWatchdog()
    if (retryTimer) clearTimeout(retryTimer)
    controller?.abort()
    handlers.onStatus('ended')
  }
}

function stopReading(reader: ReadableStreamDefaultReader<string>): void {
  void reader.cancel().catch(() => undefined)
}
